import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../components/LanguageSelector";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Fade,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuthContext } from "../contexts/AuthContext";
import { useTheme } from "@mui/material/styles";
import { usePublicLayout } from "../hooks/usePublicLayout";
import { usePublicLayoutContext } from "../contexts/PublicLayoutContext";
import InstagramQRCode from "./InstagramQRCode";

// 💎 Estilos para o tema Clean Corporate (Fundo Branco)
const cleanInputStyles = {
  input: {
    color: "#1E293B", // Texto escuro
    fontWeight: "500",
    fontSize: "1rem",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px", // Bordas sutilmente arredondadas, perfil executivo
    backgroundColor: "#F8FAFC", // Fundo do input levemente cinza
    border: "1px solid transparent",
    transition: "all 0.2s ease-in-out",
    "& fieldset": { 
      borderColor: "#E2E8F0", // Borda padrão cinza clara
      transition: "all 0.2s ease-in-out",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1", // Borda cinza ao passar o mouse
    },
    "&.Mui-focused fieldset": {
      borderColor: "#DAA520", // Borda dourada no foco
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF",
      boxShadow: "0 4px 12px rgba(218, 165, 32, 0.08)",
    },
  },
};

const Login = () => {
  const { t } = useTranslation("salon");
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const theme = useTheme();

  const [entidadeId] = useState(() => {
    const idFromUrl = new URLSearchParams(location.search).get("id");
    return idFromUrl || undefined;
  });

  const { layout, setLayout } = usePublicLayoutContext();
  const layoutApi = usePublicLayout(entidadeId);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const layoutFinal = useMemo(() => ({
    logoUrl: layout?.logoUrl || "/images/logo_dourado.png",
    logoBack: layout?.logoBack || "/images/lion_background.png",
    titulo: layout?.titulo || "Lion of Code",
    corPrimaria: layout?.corPrimaria || "#1F2937",
    corSecundaria: layout?.corSecundaria || "#374151",
    carouselImages: layout?.carouselImages || [],
  }), [layout]);

  const carouselImages = useMemo(() => {
    const baseImages =
      layoutFinal.carouselImages.length > 0
        ? layoutFinal.carouselImages
        : [layoutFinal.logoBack];

    const maxImages = 3;
    const result = [];
    let i = 0;

    while (result.length < maxImages) {
      result.push(baseImages[i % baseImages.length]);
      i++;
    }

    return result;
  }, [layoutFinal.carouselImages, layoutFinal.logoBack]);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => event.preventDefault();

  useEffect(() => {
    if (layoutApi && setLayout && entidadeId) {
      setLayout(layoutApi);
    }
  }, [layoutApi, setLayout, entidadeId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 7000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const persistContext = ({ entidadeId, unidadeId }) => {
    if (entidadeId) {
      localStorage.setItem("Entidade", String(entidadeId));
      localStorage.setItem("entidadeId", String(entidadeId));
      localStorage.setItem("selectedEntidade", String(entidadeId));
    }
    if (unidadeId) {
      localStorage.setItem("Unidade", String(unidadeId));
      localStorage.setItem("unidadeId", String(unidadeId));
      localStorage.setItem("selectedUnidade", String(unidadeId));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const emailFormatado = email.trim().toLowerCase();

    if (!emailFormatado || !password) {
      setErrorMessage(t("loginRequired"));
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/api/Auth/login", {
        email: emailFormatado,
        password,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Erro ao fazer login.");
      }

      const { token, user, permissions, entidadeId: entidadeIdRetorno, unidadeId: unidadeIdRetorno } =
        response.data?.data || {};

      const primeiroAcesso = user?.primeiroAcesso ?? false;

      if (!token) throw new Error("Token não retornado no login.");

      const entidadeFinal = entidadeIdRetorno || entidadeId;
      const unidadeFinal = unidadeIdRetorno || localStorage.getItem("selectedUnidade") || undefined;

      if (entidadeFinal) {
        persistContext({ entidadeId: entidadeFinal, unidadeId: unidadeFinal });
      }

      login(token, user, permissions || []);
      sessionStorage.setItem("senhaAtual", password);

      if (primeiroAcesso && user?.email) {
        navigate("/alterar-senha-primeiro-acesso", {
          state: { email: user.email, senhaAtual: password },
        });
      } else {
        setTimeout(() => {
          navigate("/loginfilter", { replace: true });
        }, 100);
      }
    } catch (error) {
      console.error("❌ Erro no login:", error.response?.data?.message || error.message);

      const errorMessageFromServer = error.response?.data?.message || "";
      const errorDetails = error.response?.data?.errors?.[0] || "";

      if (error.code === "API_CONFIGURATION_ERROR") {
        setErrorMessage(error.message);
      } else if (
        errorMessageFromServer.includes("Muitas tentativas falhas") ||
        errorDetails.includes("Conta temporariamente bloqueada")
      ) {
        setErrorMessage("⏳ Conta bloqueada temporariamente. Tente novamente em 15 minutos.");
      } else if (
        errorMessageFromServer.includes("Credenciais inválidas") ||
        errorDetails.includes("Email ou senha incorretos")
      ) {
        setErrorMessage("⚠️ E-mail ou senha incorretos.");
      } else {
        setErrorMessage("⚠️ Erro ao realizar o login. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100dvh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* =========================================
          ÁREA DE IMPACTO - 70% (MANTIDA INTACTA)
      ========================================= */}
      <Box
        sx={{
          width: { xs: "100%", md: "60%", lg: "70%" },
          position: "relative",
          display: { xs: "none", md: "block" },
          background: `linear-gradient(135deg, 
            rgba(184, 134, 11, 0.9) 0%, 
            rgba(218, 165, 32, 0.8) 25%,
            rgba(205, 127, 50, 0.8) 50%,
            rgba(139, 105, 20, 0.9) 75%,
            rgba(112, 66, 20, 0.95) 100%)`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
          }}
        >
          {carouselImages.map((image, index) => (
            <Box
              key={index}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: currentImageIndex === index ? 1 : 0,
                transition: "opacity 1.5s ease-in-out",
                zIndex: 1,
              }}
            />
          ))}
        </Box>

        <Box sx={{ position: "absolute", bottom: 30, left: 30, zIndex: 4 }}>
          <InstagramQRCode />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `linear-gradient(135deg, 
              rgba(184, 134, 11, 0.7) 0%, 
              rgba(218, 165, 32, 0.6) 25%,
              rgba(205, 127, 50, 0.6) 50%,
              rgba(139, 105, 20, 0.7) 75%,
              rgba(112, 66, 20, 0.8) 100%)`,
            zIndex: 2,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: "white",
            zIndex: 4,
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={layoutFinal.logoUrl}
            alt={`Logo da ${layoutFinal.titulo}`}
            sx={{
              width: { xs: "160px", md: "200px" },
              mb: 4,
              mx: "auto",
              display: "block",
              filter: "drop-shadow(0 0 20px white)",
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontWeight: "900",
              mb: 2,
              fontSize: { xs: "2.2rem", md: "3.5rem" },
              textShadow: "3px 3px 6px rgba(0,0,0,0.7)",
              fontFamily: "'Playfair Display', serif",
              letterSpacing: "1.5px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            EXCELÊNCIA & INOVAÇÃO
          </Typography>

          <Typography
            variant="h5"
            sx={{
              mb: 4,
              fontSize: { xs: "1rem", md: "1.8rem" },
              textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
              opacity: 0.95,
              fontFamily: "'Roboto', sans-serif",
              fontWeight: "300",
              letterSpacing: "1px",
            }}
          >
            Transformando o futuro com soluções tecnológicas de alta performance
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            {carouselImages.map((_, index) => (
              <Box
                key={index}
                role="button"
                aria-label={`Ir para imagem ${index + 1}`}
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: currentImageIndex === index ? "#DAA520" : "rgba(255,255,255,0.4)",
                  border: currentImageIndex === index ? "2px solid white" : "2px solid transparent",
                  transition: "all 0.4s ease",
                  cursor: "pointer",
                  boxShadow: currentImageIndex === index ? "0 0 15px rgba(218, 165, 32, 0.8)" : "none",
                  "&:hover": { transform: "scale(1.2)", backgroundColor: "#DAA520" },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* =========================================
          ÁREA DE LOGIN - 30% (CLEAN & BRANCO)
      ========================================= */}
      <Box
        sx={{
          width: { xs: "100%", md: "40%", lg: "30%" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          paddingBlock: { xs: 2, md: 1 },
          backgroundColor: "#FFFFFF", // Fundo Totalmente Branco
          position: "relative",
          zIndex: 10,
          boxShadow: "-10px 0 30px rgba(0,0,0,0.06)", // Sombra suave para separar do banner
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "400px",
            p: { xs: 2.5, sm: 4, md: 3, lg: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box textAlign="center" mb={{ xs: 2, sm: 3, md: 4 }} width="100%">
            <img
              src={layoutFinal.logoUrl}
              alt="Logo da Empresa"
              style={{
                width: "140px", // Levemente menor para um design mais limpo
                marginBottom: "20px",
                marginLeft: "auto",
                marginRight: "auto",
                display: "block",
                // Removida a sombra branca pesada que não fazia sentido no fundo branco
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: "700",
                color: "#1E293B", // Slate escuro
                fontFamily: "'Playfair Display', serif",
                letterSpacing: "0.5px",
                mb: 1
              }}
            >
              {t("loginWelcome")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748B", // Slate médio
                fontFamily: "'Roboto', sans-serif",
              }}
            >
              {t("loginPrompt")}
            </Typography>
          </Box>

          <Box sx={{ width: "100%", minHeight: "50px", mb: 2 }} aria-live="polite" role="alert">
            <Fade in={!!errorMessage}>
              <Box
                sx={{
                  display: errorMessage ? "block" : "none",
                  backgroundColor: "#FEF2F2", // Vermelho super claro
                  color: "#991B1B", // Vermelho escuro
                  borderRadius: "8px",
                  padding: "12px 16px",
                  textAlign: "center",
                  border: "1px solid #FCA5A5",
                  fontWeight: "500",
                  fontSize: "0.9rem"
                }}
              >
                {errorMessage}
              </Box>
            </Fade>
          </Box>

          <form onSubmit={handleLogin} style={{ width: "100%" }}>
            <TextField
              fullWidth
              label={t("loginEmail") }
              variant="outlined"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              InputLabelProps={{
                style: {
                  color: "#64748B",
                  fontWeight: "500",
                },
              }}
              sx={cleanInputStyles}
            />

            <TextField
              fullWidth
              label={t("loginPassword") }
              variant="outlined"
              type={showPassword ? "text" : "password"}
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="alternar visibilidade da senha"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{
                        color: "#94A3B8",
                        "&:hover": { color: "#DAA520" },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                style: {
                  color: "#64748B",
                  fontWeight: "500",
                },
              }}
              sx={cleanInputStyles}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              disableElevation
              sx={{
                mt: 4,
                mb: 2,
                borderRadius: "12px",
                padding: "14px",
                fontSize: "1rem",
                fontWeight: "bold",
                fontFamily: "'Roboto', sans-serif",
                letterSpacing: "0.5px",
                backgroundColor: layoutFinal.corPrimaria !== "#1F2937" ? layoutFinal.corPrimaria : "#DAA520",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#B8860B", // Dourado escuro no hover
                  transform: "translateY(-1px)",
                  boxShadow: "0 8px 20px rgba(218, 165, 32, 0.3)",
                },
                "&:active": { transform: "translateY(0px)" },
                transition: "all 0.2s ease-in-out",
                "&.Mui-disabled": {
                  backgroundColor: "#E2E8F0",
                  color: "#94A3B8",
                }
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "#FFFFFF" }} /> : t("loginEnter")}
            </Button>

            <Button
              fullWidth
              type="button"
              disabled={loading}
              onClick={() => navigate("/Security/recuperar-senha")}
              sx={{
                color: "#64748B",
                fontWeight: "500",
                borderRadius: "8px",
                padding: "10px",
                fontSize: "0.9rem",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  color: "#DAA520",
                },
                transition: "all 0.2s ease",
              }}
            >
              {t("loginForgot")}
            </Button>
          </form>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 2, pt: 2, borderTop: "1px solid #eef2f6" }}>
            <LanguageSelector small />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;